import { useEffect, useState, useRef } from "react";

type UseTouchActionProps = {
  containerRef: React.RefObject<HTMLDivElement>;
  onTouchAction?: (overscrollDistance: number, touchEnd: boolean) => void;
  scrollable?: boolean;
};

const useTouchAction = ({ containerRef, onTouchAction, scrollable = true }: UseTouchActionProps) => {
  const [overscrollDistance, setOverscrollDistance] = useState(0);
  const [touchEnd, setTouchEnd] = useState(false);
  const startYRef = useRef(0);
  const startScrollTopRef = useRef(0);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    if (!scrollable) return;

    const handleTouchStart = (e: TouchEvent) => {
      startYRef.current = e.touches[0].clientY;
      startScrollTopRef.current = container.scrollTop;
      setTouchEnd(false);
      setOverscrollDistance(0);
    };

    const handleTouchMove = (e: TouchEvent) => {
      const currentY = e.touches[0].clientY;
      const deltaY = currentY - startYRef.current;
      const newScrollTop = startScrollTopRef.current - deltaY;

      const isScrolledAboveTop = newScrollTop < 0;

      if (isScrolledAboveTop) {
        const overscrollAmount = Math.abs(newScrollTop);
        setOverscrollDistance(overscrollAmount);
        onTouchAction?.(overscrollAmount, false);
      } else {
        setOverscrollDistance(0);
        onTouchAction?.(0, false);
      }
    };

    const handleTouchEnd = (e: TouchEvent) => {
      setTouchEnd(true);
      onTouchAction?.(overscrollDistance, true);
    };

    container.addEventListener('touchstart', handleTouchStart);
    container.addEventListener('touchmove', handleTouchMove);
    container.addEventListener('touchend', handleTouchEnd);

    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
    };
  }, [containerRef, onTouchAction, overscrollDistance, scrollable]);

  return { overscrollDistance, touchEnd };
};

export default useTouchAction;